import { type ReactElement } from "react";
import { LayoutGrid } from "lucide-react";
import { Badge, Divider } from "../atoms/index.js";
import { EmptyState } from "../molecules/index.js";
import { cn } from "../lib/utils.js";
import type { AppLauncherEntry, AppLauncherProps } from "./launcher.types.js";

/**
 * Grid of "apps" the user can open — one card per entry. Data-blind:
 * entries are derived by the host from its module manifest.
 *
 * Which modules are visible and their order are host-owned preferences. The
 * host projects that curated roster into `apps`; this design-system component
 * remains deliberately data-blind.
 */
export function AppLauncher({
  heading,
  apps,
  onAppClick,
  utilitySection,
  emptyState,
}: AppLauncherProps): ReactElement {
  return (
    <div>
      {apps.length === 0 ? (
        (emptyState ?? (
          <EmptyState
            variant="first-use"
            icon={<LayoutGrid className="w-5 h-5" />}
            title="No apps available"
          />
        ))
      ) : (
        <>
          <div className="flex items-center gap-2 mb-6 text-pb-text-muted text-xs uppercase tracking-wider font-semibold">
            <LayoutGrid className="w-3.5 h-3.5" /> {heading}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {apps.map((app) => (
              <AppCard key={app.id} app={app} onClick={() => onAppClick(app)} />
            ))}
          </div>
        </>
      )}

      {utilitySection && utilitySection.entries.length > 0 ? (
        <>
          <Divider className="mt-8" />
          <div className="mt-8 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {utilitySection.entries.map((entry) => (
              <AppCard
                key={entry.id}
                app={entry}
                onClick={() => utilitySection.onItemClick(entry)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

interface AppCardProps {
  app: AppLauncherEntry;
  onClick: () => void;
}

function AppCard({ app, onClick }: AppCardProps): ReactElement {
  const Icon = app.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour-id={app.tourId}
      className={cn(
        "group rounded-lg border border-pb-border/80 bg-pb-card p-5 text-start shadow-sm transition-[color,background-color,background-image,border-color,box-shadow,transform] pb-hairline hover:pb-overlay-paint hover-tint press focus-ring",
        app.tint,
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <div
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-md border",
            app.tile ?? "border-pb-border/70 bg-pb-card-elevated",
            app.accent,
          )}
        >
          <Icon className="w-5 h-5" />
        </div>
        <Badge
          variant="outline"
          className="border-pb-border/70 text-pb-text-muted text-dense-2 uppercase tracking-wider"
        >
          {app.short}
        </Badge>
      </div>
      <div className="text-pb-text font-medium text-base mb-1">{app.name}</div>
      <p className="text-pb-text-secondary text-sm leading-snug">{app.blurb}</p>
    </button>
  );
}
